// src/components/DetailedListView.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES, INTERNAL_CLIENT_ID } from '../constants/routes';
import { SingleDatePicker } from './FilterControls';


// --- Helper Components ---

// Utility to format display values
const formatDisplayValue = (value, formatter) => {
  if (formatter && typeof formatter === 'function') {
    try {
      const formatted = formatter(value);
      return formatted === null || typeof formatted === 'undefined' 
        ? <span style={{ color: 'var(--pallium-icon-gray)', fontStyle: 'italic' }}>N/A</span> 
        : formatted;
    } catch (e) {
      console.error("Error in formatter:", e);
      return <span style={{ color: 'var(--pallium-error-red)', fontStyle: 'italic' }}>Format Error</span>;
    }
  }
  if (value === null || typeof value === 'undefined') {
    return <span style={{ color: 'var(--pallium-icon-gray)', fontStyle: 'italic' }}>N/A</span>;
  }
  if (Array.isArray(value)) {
    const filtered = value.filter(item => item !== null && typeof item !== 'undefined');
    return filtered.length > 0 
      ? filtered.join(', ') 
      : <span style={{ color: 'var(--pallium-icon-gray)', fontStyle: 'italic' }}>None</span>;
  }
  return String(value);
};

// Component to display truncated text
const LongTextView = ({ text, maxLength = 100 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!text) return <span style={{ color: 'var(--pallium-icon-gray)', fontStyle: 'italic' }}>No description</span>;
  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  if (text.length <= maxLength || isExpanded) {
    return (
      <div className="whitespace-pre-wrap" style={{ color: 'var(--pallium-text-primary)' }}>
        {text}
        {text.length > maxLength && (
          <button 
            onClick={toggleExpand} 
            className="ml-2 text-xs font-medium hover:underline"
            style={{ color: 'var(--pallium-link-default)' }}
          >
            (Show Less)
          </button>
        )}
      </div>
    );
  }
  return (
    <div style={{ color: 'var(--pallium-text-primary)' }}>
      {text.substring(0, maxLength)}...
      <button 
        onClick={toggleExpand} 
        className="ml-2 text-xs font-medium hover:underline"
        style={{ color: 'var(--pallium-link-default)' }}
      >
        (Show More)
      </button>
    </div>
  );
};

// --- Main Component ---
const DetailedListView = ({
    title,
    items,
    columns,
    isLoading,
    error,
    entityType,
    // New props for inline editing
    apiService,   // Pass the service instance
    clientId,       // Pass current client ID
    departmentId,   // Pass current department ID (for department context navigation)
    onItemUpdate,   // Callback to refresh parent list: (itemId, updatedField, newValue) => void
    editableColumnsConfig // Object defining editable columns: { status: { options: [], queryKey: 'updateNodeStatus', propertyName: 'status' }, ... }
  }) => {
  const navigate = useNavigate();

  const [updatingItemId, setUpdatingItemId] = useState(null); // ID of item being updated
  const [updateError, setUpdateError] = useState(null);

  const handleRowClick = (item) => {
    if (updatingItemId === item.id) return; // Don't navigate if an update for this row is in progress via dropdown
    if (!entityType || !item.id) {
        console.error("Cannot navigate: entityType or item.id missing", { entityType, itemId: item?.id });
        return;
    }
    
    // Check if item has its own clientId (for department views)
    const itemClientId = item.clientId || item.client_id;
    
    if (itemClientId) {
        // Navigate to client context if item has a clientId
        navigate(`${ROUTES.ACCOUNTS}/${itemClientId}/${entityType}/${item.id}`);
    } else if (clientId) {
        // Use context clientId if available
        navigate(`${ROUTES.ACCOUNTS}/${clientId}/${entityType}/${item.id}`);
    } else if (departmentId) {
        // If we're in a department context and item has no client, stay in department context
        console.info(`Item ${item.id} has no client ID, navigating within department context`);
        navigate(`${ROUTES.OPERATIONS}/${departmentId}/${entityType}/${item.id}`);
    } else {
        // Last resort: fallback to internal client (but this should rarely happen now)
        console.info(`Item ${item.id} has no client ID, using internal client as fallback`);
        navigate(`${ROUTES.ACCOUNTS}/${INTERNAL_CLIENT_ID}/${entityType}/${item.id}`);
    }
};

  const handleInlineUpdate = async (item, columnKey, newValue) => {
    // Allow inline updates if we have apiService and either clientId or departmentId
    // The API methods (updateNodeStatus, etc.) only need the nodeId, not clientId
    if (!apiService || !editableColumnsConfig || !editableColumnsConfig[columnKey]) {
        console.error("Inline update prerequisites not met.", {apiService, editableColumnsConfig, columnKey});
        setUpdateError("Cannot perform update: Configuration missing.");
        return;
    }
    
    const config = editableColumnsConfig[columnKey];
    if (!config.query || !config.propertyName) {
        console.error("Editable column config missing query or propertyName for key:", columnKey);
        setUpdateError(`Update configuration error for ${columnKey}.`);
        return;
    }
    
    setUpdatingItemId(item.id);
    setUpdateError(null);

    try {
        // Use the new API methods for updates
        if (config.query.includes('updateNodeStatus')) {
            await apiService.updateNodeStatus(item.id, newValue);
        } else if (config.query.includes('updateNodeSeverity')) {
            await apiService.updateIssueSeverity(item.id, newValue);
        } else if (config.query.includes('updateNodeProperty')) {
            await apiService.updateNodeProperty(item.id, config.propertyName, newValue);
        } else {
            // Fallback to legacy runQuery for any other queries
            await apiService.runQuery(config.query, {
                nodeId: item.id,
                clientId: clientId,
                newValue: newValue
            });
        }

        if (onItemUpdate) {
            onItemUpdate(item.id, config.propertyName, newValue); // Notify parent to refresh or update its state
        }
        // Optionally, show a success message briefly
    } catch (err) {
        console.error(`Failed to update ${config.propertyName} for item ${item.id}:`, err);
        setUpdateError(`Failed to update. ${err.message}`);
        // Optionally, revert optimistic update if one was made locally
    } finally {
        setUpdatingItemId(null);
    }
  };


  if (isLoading) { 
    return (
      <div 
        className="rounded-xl p-6 mt-8"
        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
      >
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--pallium-text-primary)' }}>{title}</h3>
        <div className="text-center py-4" style={{ color: 'var(--pallium-text-secondary)' }}>Loading data...</div>
      </div>
    ); 
  }
  
  if (error) { 
    return (
      <div 
        className="rounded-xl p-6 mt-8"
        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
      >
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--pallium-text-primary)' }}>{title}</h3>
        <div className="py-4 text-center" style={{ color: 'var(--pallium-error-red)' }}>Error loading data: {error}</div>
      </div>
    ); 
  }
  
  if (!items || items.length === 0) { 
    return (
      <div 
        className="rounded-xl p-6 mt-8"
        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
      >
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--pallium-text-primary)' }}>{title}</h3>
        <div className="text-center py-4" style={{ color: 'var(--pallium-text-secondary)' }}>No items found.</div>
      </div>
    ); 
  }

  return (
    <div 
      className="rounded-xl p-4 md:p-6 mt-6 md:mt-8"
      style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
    >
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--pallium-text-primary)' }}>{title}</h3>
        {updateError && (
          <div 
            className="mb-2 p-2 text-sm rounded"
            style={{ 
              backgroundColor: 'var(--pallium-alert-error-bg)', 
              color: 'var(--pallium-error-red)' 
            }}
          >
            Update Error: {updateError}
          </div>
        )}
        <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
                <thead style={{ backgroundColor: 'var(--pallium-purple-0-5)' }}>
                    <tr>
                        {columns.map((col) => (
                            <th 
                              key={col.key} 
                              scope="col" 
                              className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${col.headerClassName || ''}`} 
                              style={{ 
                                textAlign: col.align || 'left',
                                color: 'var(--pallium-primary-purple)',
                                borderBottom: '2px solid var(--pallium-purple-2)'
                              }}
                            >
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr
                            key={item.id}
                            className={`transition-colors duration-200 ${entityType ? 'cursor-pointer' : ''}`}
                            style={{ 
                              borderBottom: index < items.length - 1 ? '1px solid var(--pallium-stroke-light)' : 'none'
                            }}
                            onClick={() => entityType ? handleRowClick(item) : undefined}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--pallium-purple-0-5)'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            {columns.map((col) => {
                                const isEditable = editableColumnsConfig && editableColumnsConfig[col.key];
                                const currentItemValue = item[col.key];

                                return (
                                    <td 
                                      key={`${item.id}-${col.key}`} 
                                      className={`px-4 py-3 whitespace-normal text-sm align-top ${col.cellClassName || ''}`}
                                    >
                                        {isEditable ? (
                                            editableColumnsConfig[col.key].type === 'date' ? (
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    <SingleDatePicker
                                                        value={currentItemValue || ''}
                                                        onChange={(newValue) => {
                                                            if (newValue !== currentItemValue) {
                                                                handleInlineUpdate(item, col.key, newValue);
                                                            }
                                                        }}
                                                        disabled={updatingItemId === item.id}
                                                    />
                                                </div>
                                            ) : (
                                            <select
                                                value={currentItemValue || ''}
                                                onChange={(e) => {
                                                    e.stopPropagation(); // Important: Prevent row click
                                                    handleInlineUpdate(item, col.key, e.target.value);
                                                }}
                                                onClick={(e) => e.stopPropagation()} // Prevent row click on dropdown itself
                                                disabled={updatingItemId === item.id}
                                                className="p-1 text-sm min-w-[100px] focus:outline-none"
                                                style={{
                                                  border: '1px solid var(--pallium-border-default)',
                                                  borderRadius: 'var(--pallium-border-radius)',
                                                  backgroundColor: updatingItemId === item.id ? 'var(--pallium-bg-secondary)' : 'var(--pallium-white)',
                                                  cursor: updatingItemId === item.id ? 'not-allowed' : 'pointer'
                                                }}
                                                onFocus={(e) => {
                                                  e.target.style.borderColor = 'var(--pallium-border-focus)';
                                                  e.target.style.boxShadow = 'var(--pallium-focus-ring-primary)';
                                                }}
                                                onBlur={(e) => {
                                                  e.target.style.borderColor = 'var(--pallium-border-default)';
                                                  e.target.style.boxShadow = 'none';
                                                }}
                                            >
                                                {/* Add a default "current" option if value is not in standard list (e.g. old status) */}
                                                {currentItemValue && editableColumnsConfig[col.key].options && !editableColumnsConfig[col.key].options.includes(currentItemValue) && (
                                                    <option value={currentItemValue} disabled>{String(currentItemValue).charAt(0).toUpperCase() + String(currentItemValue).slice(1)} (Current)</option>
                                                )}
                                                {editableColumnsConfig[col.key].options && editableColumnsConfig[col.key].options.map(option => (
                                                    <option key={option} value={option}>
                                                        {String(option).charAt(0).toUpperCase() + String(option).slice(1)}
                                                    </option>
                                                ))}
                                            </select>
                                            )
                                        ) : col.key === 'description' || col.type === 'longText' ? (
                                            <LongTextView text={currentItemValue} />
                                        ) : (
                                            <span style={{ color: 'var(--pallium-text-primary)' }}>
                                              {formatDisplayValue(currentItemValue, col.formatter)}
                                            </span>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
    );
  };

export default DetailedListView;
